import { gql } from '@apollo/client';

// ==================== FRAGMENTS ====================

export const PROSPECT_FIELDS = gql`
  fragment ProspectFields on Organization {
    id
    name
    type
    status
    website
    phone
    description
    blastSyncStatus
    createdAt
    updatedAt
    primaryContact {
      id
      firstName
      lastName
      email
      phone
      title
      type
      types
    }
    contacts {
      id
      firstName
      lastName
      email
      phone
      title
      type
      types
    }
  }
`;

// ==================== QUERIES ====================

/**
 * Get paginated prospects with optional filters
 */
export const GET_PROSPECTS = gql`
  ${PROSPECT_FIELDS}
  query GetProspects(
    $type: OrganizationType
    $search: String
    $limit: Int
    $offset: Int
  ) {
    prospects(
      type: $type
      search: $search
      limit: $limit
      offset: $offset
    ) {
      items {
        ...ProspectFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

/**
 * Get a single prospect by ID
 */
export const GET_PROSPECT = gql`
  ${PROSPECT_FIELDS}
  query GetProspect($id: ID!) {
    prospect(id: $id) {
      ...ProspectFields
    }
  }
`;

// ==================== MUTATIONS ====================

/**
 * Create a new prospect
 */
export const CREATE_PROSPECT = gql`
  ${PROSPECT_FIELDS}
  mutation CreateProspect($input: CreateOrganizationInput!) {
    createProspect(input: $input) {
      ...ProspectFields
    }
  }
`;

/**
 * Update an existing prospect
 */
export const UPDATE_PROSPECT = gql`
  ${PROSPECT_FIELDS}
  mutation UpdateProspect($id: ID!, $input: UpdateOrganizationInput!) {
    updateProspect(id: $id, input: $input) {
      ...ProspectFields
    }
  }
`;

/**
 * Delete a prospect
 */
export const DELETE_PROSPECT = gql`
  mutation DeleteProspect($id: ID!) {
    deleteProspect(id: $id)
  }
`;

/**
 * Convert a prospect to an active client
 * This creates the organization in BLAST with the same UUID
 */
export const CONVERT_PROSPECT_TO_CLIENT = gql`
  ${PROSPECT_FIELDS}
  mutation ConvertProspectToClient($prospectId: ID!) {
    convertProspectToClient(prospectId: $prospectId) {
      ...ProspectFields
    }
  }
`;

// ==================== TYPES ====================

export interface Prospect {
  id: string;
  name: string;
  type: 'CFI' | 'PSP';
  status: 'PROSPECT' | 'ACTIVE' | 'INACTIVE';
  website: string | null;
  phone: string | null;
  description: string | null;
  blastSyncStatus: 'PENDING' | 'SYNCED' | 'ERROR';
  createdAt: string;
  updatedAt: string;
  primaryContact: ProspectContact | null;
  contacts: ProspectContact[];
}

export type ContactType = 'PRIMARY' | 'BILLING' | 'EXECUTIVE' | 'TECHNICAL' | 'SUPPORT' | 'DECISION_MAKER';

export interface ProspectContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  title: string | null;
  /** @deprecated Use types array instead */
  type: ContactType;
  /** Array of contact roles (multi-select) */
  types?: ContactType[];
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface ProspectsQueryResult {
  prospects: {
    items: Prospect[];
    pageInfo: PageInfo;
  };
}

export interface ProspectQueryResult {
  prospect: Prospect | null;
}

export interface CreateProspectInput {
  name: string;
  type: 'CFI' | 'PSP';
  website?: string;
  phone?: string;
}

export interface UpdateProspectInput {
  name?: string;
  type?: 'CFI' | 'PSP';
  website?: string;
  phone?: string;
  description?: string;
}

export interface CreateProspectResult {
  createProspect: Prospect;
}

export interface UpdateProspectResult {
  updateProspect: Prospect;
}

export interface DeleteProspectResult {
  deleteProspect: boolean;
}

export interface ConvertProspectResult {
  convertProspectToClient: Prospect;
}
